import {
  BAR_ASSOCIATION_OPTIONS,
  CLIENT_RATIO_CATEGORY_OPTIONS,
  CONTRACT_FILTER_OPTIONS,
  CUSTOMER_STATUS_VALUES,
  LAST_CONTACT_BUCKET_OPTIONS,
  LAWYER_COUNT_BUCKET_OPTIONS,
  LEVEL_LMH_OPTIONS,
  NEXT_ACTION_BUCKET_OPTIONS,
  ORGANIZATION_TYPE_OPTIONS,
  FIRM_PRACTICE_AREA_OPTIONS,
} from '@shared/constants/choices';
import {
  compactRecord,
  isPlainRecord,
  MAX_LIST_VIEW_SEARCH_LENGTH,
  readBoolean,
  readEnum,
  readIntegerInRange,
  readString,
  readStringArray,
  readUuidArray,
} from '@shared/lib/list-view-state';
import { ACTION_TYPE_ORDER } from '@shared/types/sales-action';
import type { FirmListQuery } from '@shared/types/firm';

// sort_by / 各 enum の許可リストは union を網羅した Record から Object.keys で導出する。
// union にメンバーを足したのに許可リストへ足し忘れると typecheck が落ちる (drift guard)。
const FIRM_SORT_FIELDS = Object.keys({
  official_name: true,
  prefecture: true,
  created_at: true,
  updated_at: true,
  lawyer_count: true,
  top_status: true,
  next_action_due_at: true,
  last_contact_at: true,
} satisfies Record<NonNullable<FirmListQuery['sort_by']>, true>) as NonNullable<
  FirmListQuery['sort_by']
>[];
const SORT_DIRECTIONS: NonNullable<FirmListQuery['sort_dir']>[] = [
  'asc',
  'desc',
];
const LAST_CONTACT_BUCKETS = optionValues(
  LAST_CONTACT_BUCKET_OPTIONS,
) as NonNullable<FirmListQuery['last_contact_bucket']>[];
const NEXT_ACTION_BUCKETS = optionValues(
  NEXT_ACTION_BUCKET_OPTIONS,
) as NonNullable<FirmListQuery['next_action_bucket']>[];
const PHONE_PRESENCES = Object.keys({
  present: true,
  missing: true,
} satisfies Record<
  NonNullable<FirmListQuery['phone_presence']>,
  true
>) as NonNullable<FirmListQuery['phone_presence']>[];
const COMPETITOR_SERVICE_PRESENCES = Object.keys({
  present: true,
  missing: true,
} satisfies Record<
  NonNullable<FirmListQuery['competitor_service_presence']>,
  true
>) as NonNullable<FirmListQuery['competitor_service_presence']>[];
const LEGAL_TRAINING_TERM_SCOPES = Object.keys({
  any: true,
  representative: true,
  oldest: true,
} satisfies Record<
  NonNullable<FirmListQuery['legal_training_term_scope']>,
  true
>) as NonNullable<FirmListQuery['legal_training_term_scope']>[];
const LAWYER_COUNT_BUCKETS = optionValues(
  LAWYER_COUNT_BUCKET_OPTIONS,
) as NonNullable<FirmListQuery['lawyer_count_buckets']>;
const ORGANIZATION_TYPES = optionValues(ORGANIZATION_TYPE_OPTIONS);
const PRACTICE_AREAS = optionValues(FIRM_PRACTICE_AREA_OPTIONS);
// 弁護士会は静的カタログ (全国52会・正式名称)。 カタログ外の保存値は破棄する
// (phantom フィルタ防止)。 列フィルタが emit する値と同一集合を単一ソースから導出。
const BAR_ASSOCIATIONS = optionValues(BAR_ASSOCIATION_OPTIONS);
const CLIENT_RATIO_CATEGORIES = optionValues(
  CLIENT_RATIO_CATEGORY_OPTIONS,
) as NonNullable<FirmListQuery['client_ratio_categories']>;
const IT_LITERACIES = [
  ...optionValues(LEVEL_LMH_OPTIONS),
  'unset',
] as NonNullable<FirmListQuery['it_literacies']>;
// `uncontracted` を含む契約フィルタ候補から導出する (save/restore で往復する)。
const CONTRACT_STATUSES = optionValues(CONTRACT_FILTER_OPTIONS) as NonNullable<
  FirmListQuery['contract_status_filter']
>[];
const MIN_PAGE_SIZE = 20;
const MAX_PAGE_SIZE = 200;
const MAX_LEGAL_TRAINING_TERM = 10_000;

export function createDefaultFirmListQuery(): FirmListQuery {
  // ステータスの既定は絞り込みなし (受注/失注を含む全9値表示)。
  return {
    sort_by: 'updated_at',
    sort_dir: 'desc',
    page: 1,
    page_size: 200,
  };
}

export function serializeFirmListQuery(
  query: FirmListQuery,
): Record<string, unknown> {
  return compactRecord({
    q: query.q?.slice(0, MAX_LIST_VIEW_SEARCH_LENGTH),
    lawyer_count_buckets: query.lawyer_count_buckets,
    bar_associations: query.bar_associations,
    product_ids: query.product_ids,
    top_sales_grades: query.top_sales_grades,
    last_contact_bucket: query.last_contact_bucket,
    next_action_bucket: query.next_action_bucket,
    next_action_types: query.next_action_types,
    cities: query.cities,
    organization_types: query.organization_types,
    practice_areas: query.practice_areas,
    client_ratio_categories: query.client_ratio_categories,
    it_literacies: query.it_literacies,
    bulk_sales_all_excluded: query.bulk_sales_all_excluded,
    is_competitor: query.is_competitor,
    competitor_service_ids: query.competitor_service_ids,
    competitor_service_presence: query.competitor_service_presence,
    assignee_account_ids: query.assignee_account_ids,
    unassigned_only: query.unassigned_only,
    phone_presence: query.phone_presence,
    legal_training_term_scope: query.legal_training_term_scope,
    legal_training_term_from: query.legal_training_term_from,
    legal_training_term_to: query.legal_training_term_to,
    contract_status_filter: query.contract_status_filter,
    sort_by: query.sort_by,
    sort_dir: query.sort_dir,
    page_size: query.page_size,
  });
}

export function validateFirmListQuery(
  value: unknown,
): Partial<FirmListQuery> {
  if (!isPlainRecord(value)) return {};

  const query: Partial<FirmListQuery> = {
    q: readString(value.q, MAX_LIST_VIEW_SEARCH_LENGTH),
    lawyer_count_buckets: readStringArray(
      value.lawyer_count_buckets,
      LAWYER_COUNT_BUCKETS,
    ),
    bar_associations: readStringArray(value.bar_associations, BAR_ASSOCIATIONS),
    product_ids: readUuidArray(value.product_ids),
    last_contact_bucket: readEnum(
      value.last_contact_bucket,
      LAST_CONTACT_BUCKETS,
    ),
    next_action_bucket: readEnum(value.next_action_bucket, NEXT_ACTION_BUCKETS),
    next_action_types: readStringArray(
      value.next_action_types,
      ACTION_TYPE_ORDER,
    ),
    cities: readStringArray(value.cities),
    organization_types: readStringArray(
      value.organization_types,
      ORGANIZATION_TYPES,
    ),
    practice_areas: readStringArray(value.practice_areas, PRACTICE_AREAS),
    client_ratio_categories: readStringArray(
      value.client_ratio_categories,
      CLIENT_RATIO_CATEGORIES,
    ),
    it_literacies: readStringArray(value.it_literacies, IT_LITERACIES),
    bulk_sales_all_excluded: readBoolean(value.bulk_sales_all_excluded),
    is_competitor: readBoolean(value.is_competitor),
    competitor_service_ids: readUuidArray(value.competitor_service_ids),
    competitor_service_presence: readEnum(
      value.competitor_service_presence,
      COMPETITOR_SERVICE_PRESENCES,
    ),
    assignee_account_ids: readUuidArray(value.assignee_account_ids),
    unassigned_only: readBoolean(value.unassigned_only),
    phone_presence: readEnum(value.phone_presence, PHONE_PRESENCES),
    contract_status_filter: readEnum(
      value.contract_status_filter,
      CONTRACT_STATUSES,
    ),
    sort_by: readEnum(value.sort_by, FIRM_SORT_FIELDS),
    sort_dir: readEnum(value.sort_dir, SORT_DIRECTIONS),
    page_size: readIntegerInRange(value.page_size, MIN_PAGE_SIZE, MAX_PAGE_SIZE),
  };

  if ('top_sales_grades' in value) {
    // 全9値 (won/lost 含む) が許可対象。空・全選択は「絞り込みなし」として保存しない。
    // 旧版 (active 7値既定) の保存値は妥当な部分集合なので、そのまま明示フィルタとして
    // 復元される (チップから解除可能)。
    const statuses = readStringArray(
      value.top_sales_grades,
      CUSTOMER_STATUS_VALUES,
    );
    query.top_sales_grades =
      statuses &&
      statuses.length > 0 &&
      statuses.length < CUSTOMER_STATUS_VALUES.length
        ? statuses
        : undefined;
  }

  normalizeAssigneeConflict(query);
  normalizeCompetitorServiceConflict(query);
  normalizeLegalTrainingTerms(query, value);
  return compactQuery(query);
}

function normalizeAssigneeConflict(query: Partial<FirmListQuery>): void {
  if (query.unassigned_only === true) {
    query.assignee_account_ids = undefined;
  } else if (query.assignee_account_ids?.length) {
    query.unassigned_only = undefined;
  }
}

function normalizeCompetitorServiceConflict(
  query: Partial<FirmListQuery>,
): void {
  if (query.competitor_service_presence === 'missing') {
    query.competitor_service_ids = undefined;
  }
}

function normalizeLegalTrainingTerms(
  query: Partial<FirmListQuery>,
  value: Record<string, unknown>,
): void {
  // 範囲外 (1..MAX_LEGAL_TRAINING_TERM 外) は clamp せず破棄する。 clamp すると
  // ユーザーが設定していない上限値が復元されたり、 cap 超の逆転ペアが (cap, cap) として
  // 生き残ってしまうため (readIntegerInRange は範囲外を undefined にする)。
  const from = readIntegerInRange(
    value.legal_training_term_from,
    1,
    MAX_LEGAL_TRAINING_TERM,
  );
  const to = readIntegerInRange(
    value.legal_training_term_to,
    1,
    MAX_LEGAL_TRAINING_TERM,
  );
  if (from !== undefined && to !== undefined && from > to) return;
  query.legal_training_term_from = from;
  query.legal_training_term_to = to;
  if (from !== undefined || to !== undefined) {
    query.legal_training_term_scope = readEnum(
      value.legal_training_term_scope,
      LEGAL_TRAINING_TERM_SCOPES,
    );
  }
}

function compactQuery(query: Partial<FirmListQuery>): Partial<FirmListQuery> {
  return compactRecord(
    query as Record<string, unknown>,
  ) as Partial<FirmListQuery>;
}

function optionValues(options: { value: string }[]): string[] {
  return options.map((option) => option.value);
}
